import { APP_SCORE_COLORS } from '@/constants/config';
import type { GradientStop } from '../types';

/**
 * Blends two CSS color values by `toWeight` (0–1) using `color-mix(in srgb)`.
 * Returns `fromColor` at weight 0 and `toColor` at weight 1.
 * @author Copilot
 */
const mixColors = (fromColor: string, toColor: string, toWeight: number): string => {
  const clampedToWeight = Math.max(0, Math.min(1, toWeight));
  if (clampedToWeight <= 0) return fromColor;
  if (clampedToWeight >= 1) return toColor;
  if (fromColor === toColor) return fromColor;

  const fromWeightPercent = ((1 - clampedToWeight) * 100).toFixed(2);
  const toWeightPercent = (clampedToWeight * 100).toFixed(2);

  return `color-mix(in srgb, ${fromColor} ${fromWeightPercent}%, ${toColor} ${toWeightPercent}%)`;
};

/**
 * Interpolates a color from a sorted array of gradient stops at position `t` (0–1).
 * Falls back to `APP_SCORE_COLORS.L1` when the stops array is empty.
 * @author Copilot
 */
export const getStopColorAt = (stops: GradientStop[], t: number): string => {
  if (stops.length === 0) return APP_SCORE_COLORS.L1;
  if (t <= stops[0].offset) return stops[0].color;
  if (t >= stops[stops.length - 1].offset) return stops[stops.length - 1].color;

  for (let i = 0; i < stops.length - 1; i++) {
    const current = stops[i];
    const next = stops[i + 1];

    if (t >= current.offset && t <= next.offset) {
      const localRange = next.offset - current.offset;
      if (localRange <= 0) return next.color;

      const localT = (t - current.offset) / localRange;
      return mixColors(current.color, next.color, localT);
    }
  }

  return stops[stops.length - 1].color;
};
